import * as Sentry from 'mage-deps/@sentry/browser'
import { createBrowserHistory } from 'mage-deps/history'
import { selectors as coreSelectors, init as coreInit } from 'mage-core' // this should remain above mage-components else IE breaks MPP-3342
import { Root, AuthenticationContextProvider } from 'mage-components'
import { CMSContextProvider } from 'mage-cms'
import { init as insightsInit } from 'mage-insights'
import { FetchEducationFeatures } from 'education-tools'
import * as R from 'ramda'
import React from 'react'
import { render } from 'react-dom'
import { Provider } from 'react-redux'
import uuid from 'uuid/v4'

import 'normalize.css'
import './base.css'

import configureState from './state'
import configureStore from './store'

import ErrorBoundary from './components/ErrorBoundary'

const initSentry = (environment, region, sessionId) => {
  Sentry.init({
    dsn: 'https://fbe6f99f4edc48218dc43f4c710783aa@sentry.io/1352785',
    environment,
    normalizeDepth: 5
  })
  Sentry.configureScope(function (scope) {
    scope.setTags({
      clientId: window.clientId,
      sessionId,
      region
    })
  })
}

export default (App, { isPublic = false, title, ...options } = {}) => {
  const sessionId = initializeSessionId()
  const isSentryEnabled = R.pathOr(true, ['config', 'common', 'sentry', 'enabled'], window)
  if (isSentryEnabled) {
    const environment = R.pathOr('production', ['config', 'common', 'environment', 'type'], window)
    const region = R.pathOr('eu', ['config', 'common', 'environment', 'region'], window).toLowerCase()
    initSentry(environment, region, sessionId)
  }

  const {
    createState = () => ({}),
    createReducers = () => ({}),
    createMiddlewares = () => ([]),
    epics = [],
    isWide = false,
    translations = {},
    w = window,
    container = document.getElementById('root'),
    Framework
  } = options

  const state = configureState(w, createState, translations, { sessionId })
  const history = createBrowserHistory({
    basename: coreSelectors.getBasename(state)
  })
  const store = configureStore(state, history, createReducers, createMiddlewares, epics)

  coreInit(store, window.location)
  insightsInit(window.clientId, state.config, history)
  const designVersion = coreSelectors.getDesignVersion(state)

  render(
    (
      <Provider store={store}>
        <ErrorBoundary designVersion={designVersion}>
          <FetchEducationFeatures>
            <CMSContextProvider>
              <AuthenticationContextProvider initialAuthenticationRequired={!isPublic}>
                <Root App={App} history={history} isWide={isWide} title={title} Framework={Framework} />
              </AuthenticationContextProvider>
            </CMSContextProvider>
          </FetchEducationFeatures>
        </ErrorBoundary>
      </Provider>
    ),
    container
  )
}

function isSessionStorageAvailable () {
  try {
    return !!window.sessionStorage
  } catch (error) {
    return false
  }
}

function initializeSessionId () {
  if (isSessionStorageAvailable()) {
    try {
      const SESSION_ID_KEY = 'mage-app.sessionId'
      const storageSessionId = window.sessionStorage.getItem(SESSION_ID_KEY)
      const sessionId = storageSessionId || uuid()

      if (!storageSessionId) {
        window.sessionStorage.setItem(SESSION_ID_KEY, sessionId)
      }

      return sessionId
    } catch (error) {
      return uuid()
    }
  } else {
    return uuid()
  }
}
